import { useCallback, useEffect, useState } from 'react'

import { useChatChannelConfig } from '@liveHelper/context/ChatChannelConfigContext'
import { paramsSelector, useStore } from '@liveHelper/models/store'
import { getAllAgentsInVideoCall } from '@liveHelper/services/rest/apiService'

/**
 * Hook to determine if the chat-only mode should be enabled.
 * @return A tuple containing:
 *   - A boolean indicating if chat-only mode is active.
 *   - A function to re-check and update the chat-only status.
 */
export const useChatOnly = () => {
  const { initial_actions, api_host } = useStore(paramsSelector)
  const chatChannelConfig = useChatChannelConfig()

  const [chatOnly, setChatOnly] = useState(initial_actions === 'chat_only')

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- advisory only (React Compiler not enabled); memoization intentionally kept
  const checkChatOnly = useCallback(async () => {
    if (
      chatChannelConfig?.config?.ensure_agents_not_busy &&
      chatChannelConfig?.id
    ) {
      try {
        const { all_agents_in_video_call } = await getAllAgentsInVideoCall(
          chatChannelConfig.id,
          api_host,
        )
        setChatOnly(all_agents_in_video_call)
        // Return the value for immediate use
        return all_agents_in_video_call
      } catch (error) {
        // Do nothing on error
      }
    }

    setChatOnly(false)
    return false
  }, [
    chatChannelConfig?.config?.ensure_agents_not_busy,
    chatChannelConfig?.id,
    api_host,
  ])

  // Initial check on mount if not already chat_only
  useEffect(() => {
    if (initial_actions !== 'chat_only') {
      checkChatOnly()
    }
  }, [initial_actions, checkChatOnly])

  return [chatOnly, checkChatOnly] as const
}
